'use client'

import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'

/**
 * Settings container: static side panel on md+, left-anchored slide-in overlay on
 * phones. `absolute` within the create view's `overflow-hidden` root so the
 * off-screen state is clipped (no horizontal page scroll) and it sits below the header.
 *
 * md is the same point the header switches to its full nav + dropdown, so the whole
 * app changes shape once rather than twice.
 */
export default function SettingsDrawer({ open, onClose, children }) {
  const { t } = useTranslation()
  return (
    <>
      {/* Tap-to-close catcher (overlay breakpoints only). z-40 < panel's z-50 so only
          the exposed feed is dismissable; transparent to keep the no-dim look. */}
      {open && (
        <div
          className="md:hidden absolute inset-0 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        // Slide transform is scoped to max-md: a transform on the static panel would
        // become the containing block for position:fixed modals rendered inside, clipping
        // them to the panel. Bottom padding reserves scroll space equal to the
        // cookie-consent banner height (0 when it isn't showing) so the last controls
        // clear it instead of being covered — plus the on-screen keyboard, so the
        // panel's lower inputs (negative prompt and friends) can be scrolled out from
        // behind it rather than being unreachable while typing.
        style={{
          paddingBottom:
            'calc(var(--consent-banner-height, 0px) + var(--pc-keyboard-inset, 0px))',
        }}
        className={`absolute inset-y-0 left-0 z-50 h-full w-full overflow-y-auto overscroll-y-contain bg-[var(--middle-bg-color)] max-md:transition-transform max-md:duration-300 max-md:ease-out ${
          open ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'
        } md:static md:z-auto md:h-full md:w-[320px] md:shrink-0 md:border-r md:border-[var(--foreground-bg-color)] lg:w-[380px]`}
      >
        {/* phone-only header — the static panel is persistent so needs no back chevron */}
        <div className="md:hidden sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-[var(--middle-bg-color)] border-b border-[var(--foreground-bg-color)]">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('closeSettings')}
            className="h-9 w-9 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] text-white hover:bg-[var(--secondary-bg-color)] transition-colors"
          >
            <FaChevronLeft />
          </button>
          <span className="text-white font-bold text-sm">{t('settings')}</span>
        </div>

        {children}
      </aside>
    </>
  )
}
